/**
 * BasicInfoDrawer — Edit resident basic info form.
 *
 * Editable fields:
 *   First Name, Last Name, Nickname, Phone, Email, Notes, EHR ID, Move-in Date
 *
 * Validation:
 *   - First/last name required, alphanumeric + space/hyphen/underscore
 *   - Phone: 10 digits (dashes optional)
 *   - Move-in date: UTC handling via tzOffset
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { convertIsoUTCToLocalIsoDate } from '../../../../../libs.jsx';
import Drawer from '../../../../../components/v2/Drawer.jsx';

const BasicInfoDrawer = ({
  open, onClose, onSave, saving,
  firstName: initFirst, lastName: initLast, nickName: initNick,
  phone: initPhone, email: initEmail, notes: initNotes,
  ehrId: initEhr, moveInDate: initMoveIn, tzOffset, offsiteActive,
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nickName, setNickName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [ehrId, setEhrId] = useState('');
  const [moveInDate, setMoveInDate] = useState('');
  const [errors, setErrors] = useState({});

  // Populate form only when drawer opens (false -> true)
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setFirstName(initFirst || '');
      setLastName(initLast || '');
      setNickName(initNick || '');
      setPhone(initPhone || '');
      setEmail(initEmail || '');
      setNotes(initNotes || '');
      setEhrId(initEhr || '');
      // Convert UTC move-in date to local ISO date for date input
      setMoveInDate(
        initMoveIn && !['0000-00-00 00:00:00'].includes(initMoveIn)
          ? convertIsoUTCToLocalIsoDate(initMoveIn, tzOffset)
          : ''
      );
      setErrors({});
    }
    prevOpen.current = open;
  });

  const namePattern = /^[0-9a-zA-Z_\- ]+$/;
  const phonePattern = /^[0-9]{3}-?[0-9]{3}-?[0-9]{4}$/;

  const validate = useCallback(() => {
    const errs = {};
    if (!firstName.trim()) {
      errs.firstName = 'First name is required.';
    } else if (!namePattern.test(firstName.trim())) {
      errs.firstName = 'Only letters, numbers, spaces, hyphens, and underscores.';
    }
    if (!lastName.trim()) {
      errs.lastName = 'Last name is required.';
    } else if (!namePattern.test(lastName.trim())) {
      errs.lastName = 'Only letters, numbers, spaces, hyphens, and underscores.';
    }
    if (nickName.trim() && !namePattern.test(nickName.trim())) {
      errs.nickName = 'Only letters, numbers, spaces, hyphens, and underscores.';
    }
    if (offsiteActive && !phone.trim()) {
      errs.phone = 'Phone number is required when Offsite is enabled.';
    } else if (phone.trim() && !phonePattern.test(phone.trim())) {
      errs.phone = 'Phone must be 10 digits (dashes optional).';
    }
    if (ehrId.trim() && !namePattern.test(ehrId.trim())) {
      errs.ehrId = 'Only letters, numbers, spaces, hyphens, and underscores.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [firstName, lastName, nickName, phone, ehrId, offsiteActive]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      nickName: nickName.trim(),
      phone: phone.trim(),
      email: email.trim(),
      notes: notes.trim(),
      ehrId: ehrId.trim(),
      moveInDate: moveInDate || null,
    });
  }, [validate, onSave, firstName, lastName, nickName, phone, email, notes, ehrId, moveInDate]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Edit Resident Info"
      backdrop={false}
      bodyAs="form"
      bodyProps={{ onSubmit: handleSubmit }}
      footer={(
        <>
          <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="dp-btn dp-btn--primary" disabled={saving}>
            {saving ? 'Saving\u2026' : 'Save Changes'}
          </button>
        </>
      )}
    >
      {/* First Name */}
      <div className="dp-drawer__field">
        <label className="dp-drawer__label">
          First Name <span className="dp-drawer__required">*</span>
        </label>
        <input
          type="text"
          className={`dp-drawer__input${errors.firstName ? ' is-invalid' : ''}`}
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First name"
          autoFocus
        />
        {errors.firstName ? <p className="dp-drawer__error">{errors.firstName}</p> : null}
      </div>

      {/* Last Name */}
      <div className="dp-drawer__field">
        <label className="dp-drawer__label">
          Last Name <span className="dp-drawer__required">*</span>
        </label>
        <input
          type="text"
          className={`dp-drawer__input${errors.lastName ? ' is-invalid' : ''}`}
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Last name"
        />
        {errors.lastName ? <p className="dp-drawer__error">{errors.lastName}</p> : null}
      </div>

      {/* Nickname */}
      <div className="dp-drawer__field">
        <label className="dp-drawer__label">Nickname</label>
        <input
          type="text"
          className={`dp-drawer__input${errors.nickName ? ' is-invalid' : ''}`}
          value={nickName}
          onChange={(e) => setNickName(e.target.value)}
          placeholder="Nickname"
        />
        {errors.nickName ? <p className="dp-drawer__error">{errors.nickName}</p> : null}
      </div>

      {/* Phone */}
      <div className="dp-drawer__field">
        <label className="dp-drawer__label">
          Phone{offsiteActive ? <span className="dp-drawer__required"> *</span> : null}
        </label>
        <input
          type="text"
          className={`dp-drawer__input${errors.phone ? ' is-invalid' : ''}`}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="555-123-4567"
        />
        {errors.phone ? <p className="dp-drawer__error">{errors.phone}</p> : null}
      </div>

      {/* Email */}
      <div className="dp-drawer__field">
        <label className="dp-drawer__label">Email</label>
        <input
          type="email"
          className="dp-drawer__input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@example.com"
        />
      </div>

      {/* Notes */}
      <div className="dp-drawer__field">
        <label className="dp-drawer__label">Notes</label>
        <textarea
          className="dp-drawer__textarea"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes about this resident"
          rows={4}
          maxLength={2200}
        />
      </div>

      {/* EHR ID */}
      <div className="dp-drawer__field">
        <label className="dp-drawer__label">EHR ID</label>
        <input
          type="text"
          className={`dp-drawer__input${errors.ehrId ? ' is-invalid' : ''}`}
          value={ehrId}
          onChange={(e) => setEhrId(e.target.value)}
          placeholder="EHR External ID"
        />
        {errors.ehrId ? <p className="dp-drawer__error">{errors.ehrId}</p> : null}
      </div>

      {/* Move-in Date */}
      <div className="dp-drawer__field">
        <label className="dp-drawer__label">Move-in Date</label>
        <input
          type="date"
          className="dp-drawer__input"
          value={moveInDate}
          onChange={(e) => setMoveInDate(e.target.value)}
        />
      </div>

      {/* Footer */}
    </Drawer>
  );
};

export default BasicInfoDrawer;
